// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from "./axios";
import { toast } from "sonner";
import { showError, showSuccess } from "../actions/globalActions";
import cookie from "../utils/cookie";

const baseUrl = `${process.env.API_URL}/api/v1`;
axios.defaults.headers.common["Authorization"] = `Bearer ${cookie.get("token")}`;

const createAuthHeader = (token) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const useTeamDivisionStore = create(immer((set) => ({
  teamDivisions: [],
  // Per-division capacity stats (enriched RegistrationTeams + effective config)
  // from the public stats endpoint, used to filter to qualified (non-pending) teams.
  divisionStats: null,
  // Methods to manipulate the state
  setTeamDivisions: (teamDivisions) => {
    set((state) => {
      state.teamDivisions = teamDivisions;
    });
  },
  setTeamDivision: (id, teamDivision) => {
    set((state) => {
      const foundIndex = state.teamDivisions.findIndex(td => td._id === id);
      if (foundIndex !== -1) {
        state.teamDivisions[foundIndex] = teamDivision;
      }
    });
  },

  getTeamDivisionById: async (id, options = {}) => {
    try {
      const { include = 'division,registrationTeam,registration' } = options;
      const query = include ? `?include=${include}` : '';
      const { data } = await axios.get(`${baseUrl}/divisionTeams/${id}${query}`);
      set((state) => {
        const index = state.teamDivisions.findIndex(td => td._id === id);
        if (index !== -1) {
          state.teamDivisions[index] = data.result;
        } else {
          // Add the team division if it doesn't exist in the array
          state.teamDivisions.push(data.result);
        }
      });
    } catch (error) {
      console.error("Error in getTeamDivisionById:", error);
    }
  },

  // Asynchronous actions
  getTeamDivisionsByDivisionId: async (id, options = {}) => {
    try {
      const { include = 'players,division,team' } = options;
      const { data } = await axios.get(`${baseUrl}/divisionTeams/division/${id}?include=${include}`);
      set((state) => {
        // Remove existing teams for this division to avoid duplicates
        state.teamDivisions = state.teamDivisions.filter(team => {
          const teamDivisionId = typeof team.division === 'object' ? team.division._id : team.division;
          return teamDivisionId !== id;
        });
        // Add new teams for this division
        state.teamDivisions.push(...data.result);
      });
    } catch (error) {
      console.error("Error in getTeamDivisionsByDivisionId:", error);
    }
  },
  // Capacity/payment stats for a division (public endpoint, no PII): returns
  // enriched RegistrationTeams (`paymentReceived`/`isFree`) + effective `config`.
  getDivisionStats: async (id) => {
    try {
      const { data } = await axios.get(`${baseUrl}/registrationTeams/division/${id}/stats`);
      set((state) => {
        state.divisionStats = data.result;
      });
    } catch (error) {
      console.error("Error in getDivisionStats:", error);
    }
  },
  getTeamDivisionsByRegistrationId: async (id, token, options = {}) => {
    try {
      const { include = 'players,division,team' } = options;
      const { data } = await axios.get(`${baseUrl}/divisionTeams/registration/d/${id}?include=${include}`, createAuthHeader(token));
      set((state) => {
        state.teamDivisions = data.result;
      });
    } catch (error) {
      console.error("Error in getTeamDivisionsByRegistrationId:", error);
    }
  },
  getTeamDivisionByRegistrationTeamId: async (id, token) => {
    try {
      const { data } = await axios.get(`${baseUrl}/divisionTeams/registrationTeam/d/${id}?include=division,team`, createAuthHeader(token));
      set((state) => {
        state.teamDivision = data.result;
      });
    } catch (error) {
      console.error("Error in getTeamDivisionByRegistrationTeamId:", error);
    }
  },
  getTeamDivisionsBySeasonId: async (id, options = {}) => {
    try {
      // Default include=players so consumers like the Organize page can show
      // individual/doubles player names (via getTeamDisplayName) when teams
      // were auto-created with the zqtm_ prefix (excludeTeamName flow).
      const { include = 'players,division,team,registrationTeam' } = options;
      const query = include ? `?include=${include}` : '';
      const { data } = await axios.get(`${baseUrl}/divisionTeams/league/${id}${query}`);
      set((state) => {
        state.teamDivisions = state.teamDivisions.filter((team) => {
          const teamLeagueId = typeof team.league === 'object' ? team.league._id : team.league;
          return teamLeagueId !== id;
        });
        state.teamDivisions.push(...data.result);
      });
    } catch (error) {
      console.error("Error in getTeamDivisionsBySeasonId:", error);
    }
  },
  addTeamDivision: (getToken) => {
    return async (divisionTeam) => {
      try {
        const token = await getToken();
        const { error, result, status } = (await axios.post(`${baseUrl}/divisionTeams/d`, divisionTeam, createAuthHeader(token))).data;
        error ? toast.error(error) : toast.success(`Successfully Added!`);
        set((state) => {
          state.teamDivisions.push(result);
        });
        return result;
      } catch (error) {
        showError("Error", "Add Error");
        console.error("Error in addTeamDivision:", error);
        throw error;
      }
    }
  },

  updateTeamDivision: (getToken) => {
    return async (divisionTeam, registrationTeamId) => {
      try {
        const token = await getToken();
        const { error, result, status } = (await axios.put(`${baseUrl}/divisionTeams/d/${divisionTeam._id}?registrationTeamId=${registrationTeamId}`, divisionTeam, createAuthHeader(token))).data;
        error ? toast.error(error) : toast.success(`Successfully Updated!`);  
        set((state) => {
          const index = state.teamDivisions.findIndex(td => td._id === divisionTeam._id);
          if (index !== -1) {
            state.teamDivisions[index] = result;
          }
        });
      } catch (error) {
        showError("Error", "Update Error");
        console.error("Error in updateTeamDivision:", error);
      }
    }
  },

  deleteTeamDivision: (getToken) => {
    return async (id, deleteRegistrationTeam) => {
      try {
        const token = await getToken();
        await axios.delete(`${baseUrl}/divisionTeams/d/${id}?registrationTeam=${deleteRegistrationTeam ? deleteRegistrationTeam : ''}`, createAuthHeader(token));
        toast.success(`Successfully Removed!`);  
        set((state) => {
          state.teamDivisions = state.teamDivisions.filter(td => td._id !== id);
        });
      } catch (error) {
        console.error("Error in deleteTeamDivision:", error);
      }
    }
  },

  // Create/link a RegistrationTeam for the team manager and email them a
  // registration invite (also used to resend). Returns the (populated)
  // registrationTeam so callers can reflect the new link locally.
  inviteManager: (getToken) => {
    return async (divisionTeamId, { email, firstName, lastName, allowLateRegistration } = {}) => {
      try {
        const token = await getToken();
        const { error, result } = (await axios.post(
          `${baseUrl}/divisionTeams/d/${divisionTeamId}/inviteManager`,
          { email, firstName, lastName, allowLateRegistration },
          createAuthHeader(token),
        )).data;
        if (error) { toast.error(error); return { error }; }
        toast.success('Invitation sent');
        const rt = result && result.registrationTeam;
        if (rt) {
          set((state) => {
            const index = state.teamDivisions.findIndex(td => td._id === divisionTeamId);
            if (index !== -1) state.teamDivisions[index].registrationTeam = rt;
          });
        }
        return { result: rt };
      } catch (err) {
        toast.error('Failed to send invitation');
        console.error('Error in inviteManager:', err);
        return { error: err };
      }
    }
  },

  // Email selected players a "finish your registration" link (sign waiver / pay).
  // Backend skips players with nothing outstanding and returns { sent, skipped }.
  sendFinishRegistrationEmails: (getToken) => {
    return async (registrationTeamId, playerEmails = []) => {
      try {
        const token = await getToken();
        const { error, result } = (await axios.post(
          `${baseUrl}/registrationTeams/d/${registrationTeamId}/sendFinishRegistrationEmails`,
          { playerEmails },
          createAuthHeader(token),
        )).data;
        if (error) { toast.error(error); return { error }; }
        const sent = (result && result.sent) || 0;
        const skipped = (result && result.skipped) || 0;
        toast.success(
          `Sent to ${sent} player${sent === 1 ? '' : 's'}${skipped ? ` · ${skipped} already complete` : ''}`,
        );
        return { result };
      } catch (err) {
        toast.error('Failed to send finish-registration emails');
        console.error('Error in sendFinishRegistrationEmails:', err);
        return { error: err };
      }
    }
  },

})));

export default useTeamDivisionStore;